import React from 'react';
import PropTypes from 'prop-types';
import {InfoItem} from '@/components/molecules';
import {COLOR_CODE} from '@/utils/constants/colors';
import {
  DEFAULT_VALUES,
  FIELD_LABELS,
  CSS_CLASSES,
  LANGUAGE_CONFIG
} from '../ReviewConstants';

/**
 * LanguageRewardText component - Displays reward text in multiple languages
 * @param {Object} rewardText - Reward text data containing language-specific content
 * @param {Array} languages - Array of language objects with code and language properties
 * @returns {JSX.Element} The rendered LanguageRewardText component
 */
const LanguageRewardText = ({rewardText, languages}) => {
  if (!rewardText || Object.keys(rewardText).length === 0) {
    return (
      <div className={CSS_CLASSES.COMMON.GRID_3_COLS}>
        <InfoItem label='Reward Text' value={DEFAULT_VALUES.NO_CONTENT} />
      </div>
    );
  }

  const hasAllowedLanguages = Array.isArray(languages) && languages.length > 0;
  const entries = hasAllowedLanguages
    ? languages
        .map((lang) => {
          const langCode = lang.code;
          if (!langCode || langCode === 'enAutofillDisabled') return null;
          return [langCode, rewardText[langCode]];
        })
        .filter(Boolean)
    : Object.entries(rewardText).filter(
        ([langCode]) => langCode !== 'enAutofillDisabled'
      );

  if (entries.length === 0) {
    return (
      <div className={CSS_CLASSES.COMMON.GRID_3_COLS}>
        <InfoItem label='Reward Text' value={DEFAULT_VALUES.NO_CONTENT} />
      </div>
    );
  }

  return (
    <div className={CSS_CLASSES.COMMON.GRID_3_COLS}>
      {entries.map(([langCode, langData]) => {
        const language =
          languages?.find((lang) => lang.code === langCode) ||
          languages?.find(
            (lang) =>
              langCode &&
              lang.code?.split('_')[0] === langCode.split('_')[0] &&
              lang.code !== 'en'
          );
        const languageName = language?.language || langCode;

        return (
          <div key={langCode} className='grid'>
            <span
              className={CSS_CLASSES.COMMON.PADDING_TOP}
              style={{color: COLOR_CODE._EFEFEF}}>
              {LANGUAGE_CONFIG.DISPLAY.PREFIX}
              <b>{languageName}</b>
            </span>
            <InfoItem
              label={FIELD_LABELS.DISPLAY.TITLE}
              value={langData?.title || DEFAULT_VALUES.FALLBACK}
            />
            <InfoItem
              label={FIELD_LABELS.DISPLAY.DESCRIPTION}
              value={langData?.description || DEFAULT_VALUES.FALLBACK}
            />
          </div>
        );
      })}
    </div>
  );
};

LanguageRewardText.propTypes = {
  rewardText: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string
    })
  }),
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      language: PropTypes.string
    })
  )
};

export default LanguageRewardText;
